<template>
  <div>
    <!-- Inline -->
    <div class="row q-mb-md">
      <!-- Filters hide/show button -->
      <div class="col-6">
        <div class="row" style="justify-content: flex-start">
          <div>
            <q-btn
              @click="showFilters();"
              :text-color="hideFilters ? 'grey-5' : 'white'"
              class="neutral-btn ctrl-button btn-compact"
              :class="{ primary: !hideFilters }"
              icon="filter_list"
              :icon-right="hideFilters ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
            />
          </div>
          <!-- General searchfield -->
          <div class="q-ml-md" style="flex-grow: 1">
            <q-input
              :disable="!hideFilters || activeFilters"
              color="grey-5"
              v-model="searchTerm"
              @input="applyFilters"
              :placeholder="getString('module.blog.list.filter.label')"
            />
          </div>
        </div>
      </div>
      <!-- Add button -->
      <div class="col-6" style="justify-content: flex-end">
        <q-btn v-if="$store.state.meStore.me.rights.includes(`${permission}.add`)"
          class="capitalize ctrl-button right"
          color="ctrl-turquoise"
          :label="`${$store.state.stringsStore.strings.action.add}`"
          @click="$router.push(`/${module}/add`)"
          icon-right="add"
        />
        <q-btn v-if="$store.state.meStore.me.rights.includes('press.delete')"
          class="capitalize ctrl-button bg-ctrl-grey right q-mr-md"
          color="ctrl-turquoise"
          :disabled="!rowSelection.length"
          :label="$store.state.stringsStore.strings.list.actions.label"
          :class="{ 'button-active': rowSelection.length }">
            <q-icon size="large" name="mdi-chevron-down"/>
            <i v-show="rowSelection.length" class="counter">{{rowSelection.length}}</i>
            <!-- Direct child of target -->
            <q-popover anchor="bottom middle" self="top middle">
              <q-list link>
                <q-item
                  :class="{ 'disabled': !rowSelection.length }"
                  class="toolbar-list-item" @click.native="removeRows('posts')">
                  <q-icon color="positive" size="large" name="delete"/>
                  {{$store.state.stringsStore.strings.action.remove}}
                </q-item>
              </q-list>
            </q-popover>
        </q-btn>
      </div>
    </div>
    <!-- Filters block -->
    <transition name="fade">
    <div v-if="!hideFilters" class="table-block q-mb-md">
      <div class="row q-pa-md" style="justify-content: space-between;">
        <!-- Searchfield -->
        <div class="col-6 q-pa-md">
          <q-input color="primary"
            v-model="filters.title"
            @input="applyFilters"
            float-label="Title"/>
        </div>
        <!-- Searchfield -->
        <div class="col-6 q-pa-md">
          <q-input color="primary"
          v-model="filters.url"
          @input="applyFilters"
          float-label="Url"/>
        </div>
      </div>
      <div class="row q-pb-md q-pl-md q-pr-md">
        <!-- Select field -->
        <div class="col-6 q-pb-md q-pl-md q-pr-md">
          <q-select
            multiple
            v-model="filters.status"
            @input="applyFilters"
            :options="statusOptions"
            float-label="Status"/>
        </div>
      </div>
      <div class="row q-pa-md" style="justify-content: space-between;">
        <div class="q-pa-md" style="vertical-align: middle;">
          <q-btn
            :disable="!activeFilters"
            @click="resetFilters()"
            class="capitalize ctrl-button"
            :color="activeFilters ? 'primary' : 'grey-4'"
            label="Reset"
          />
        </div>
      </div>
    </div>
    </transition>
    <!-- Data table -->
    <div class="q-table" id="myTable">
      <vuetable
        ref="vuetable"
        class="table-block q-mb-md"
        :class="{ 'is-loading': isLoading }"
        :table-height="tableHeight"
        :api-url="endpoint"
        pagination-path
        :api-mode="true"
        :css="css.table"
        :noDataTemplate="getString('list.nothingFound').replace('%records%', 'posts')"
        :http-fetch="fetchData"
        :fields="columns"
        :row-class="selectable ? applyRowClass : null"
        @vuetable:loading="onLoading"
        @vuetable:load-success="onSuccess"
        @vuetable:load-error="onError"
        @vuetable:pagination-data="onPaginationData"
        @vuetable:row-clicked="onRowClicked">
        <!-- Custom fields -->
        <!-- Name -->
        <div slot="title" slot-scope="props">
          <div v-html="highlight(props.rowData.title, filters.title)"></div>
        </div>
        <!-- Url -->
        <div slot="url" slot-scope="props">
          <div v-html="highlight(props.rowData.url, filters.url)"></div>
        </div>
        <!-- Last updated -->
        <div slot="datetime" slot-scope="props">
          <div class="text-ellipsis" v-html="(props.rowData.datetime.update) ?
            tableFormatTime(props.rowData.datetime.update) :
            tableFormatTime(props.rowData.datetime.added)">
          </div>
        </div>
        <!-- Target audience -->
        <div slot="target-audience" slot-scope="props">
          {{ mapTargets(props.rowData.targets) }}
        </div>
        <!-- Datetime online -->
        <div slot="datetime-online" slot-scope="props">
          <div class="text-ellipsis" v-html="(props.rowData.datetime && props.rowData.status !== 'draft') ?
            tableFormatTime(props.rowData.datetime) :
            'Concept'
            ">
          </div>
        </div>
        <div slot="locked" slot-scope="props">
          <div v-if="props.rowData.locked">
            <q-icon size="large" name="lock"/>
            <q-tooltip :delay="200">
              <div class="lock-info__tooltip"></div>
                {{ getString('content.lock.overview.info').replace('%name%', fullname(props.rowData.locked.user.name)).replace('%time%', tableFormatTime(props.rowData.locked.since)) }}
            </q-tooltip>
          </div>
          <q-icon v-else size="large" name="lock_open"/>
        </div>
      </vuetable>
      <!-- Pagination -->
      <div class="row pagination-bottom" id="myPagination">
        <!-- Per page select -->
        <div class="col">
          <div class="row">
            <span class="q-mr-sm grey">
              {{ $store.state.stringsStore.strings.list.itemsPerPage }}
            </span>
            <q-select
              class="btn-compact select-small"
              hide-underline
              color="grey"
              v-model="perPage"
              :options="perPageOptions"
            />
          </div>
        </div>
        <div class="col right-margin">
          <vuetable-pagination
            :on-each-side="1"
            :css="css.pagination"
            @vuetable-pagination:change-page="onChangePage"
            ref="pagination">
          </vuetable-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import getString from '@/helpers/GetStrings';
import Vuetable from 'vuetable-2';
import VuetablePagination from 'vuetable-2/src/components/VuetablePagination.vue';

// Mixins
import tableMixin from '@/mixins/tables/tableMixin';
import pagination from '@/mixins/tables/pagination';
import searchHighlight from '@/mixins/tables/searchHighlight';
import fetchData from '@/mixins/tables/fetchData';
import selectRows from '@/mixins/tables/selectRows';
import common from '../common';

export default {
  mixins: [
    tableMixin,
    pagination,
    searchHighlight,
    fetchData,
    selectRows,
    common,
  ],
  components: {
    Vuetable,
    VuetablePagination,
  },
  methods: {
    mapTargets(array) {
      const targets = array.map(target => target.label);
      return targets.join(', ');
    },
    getString,
  },
  data() {
    return {
      isLoading: false,
      statusOptions: [
        {
          label: 'Concept',
          value: 'draft',
        },
        {
          label: 'Published',
          value: 'published',
        },
        {
          label: 'Unpublished',
          value: 'unpublished',
        },
      ],
      columns: [
        {
          name: '__slot:title',
          title: getString('title'),
          sortField: 'title',
          titleClass: 'text-left sortable vcol-m',
          dataClass: 'text-left vcol-m text-ellipsis',
        },
        {
          name: '__slot:url',
          title: getString('url'),
          sortField: 'url',
          titleClass: 'text-left sortable vcol-l',
          dataClass: 'text-left vcol-l text-ellipsis',
        },
        {
          name: '__slot:target-audience',
          title: getString('editor.block.header.targets'),
          titleClass: 'text-left sortable vcol-l',
          dataClass: 'text-left vcol-l text-ellipsis',
        },
        {
          name: '__slot:datetime-online',
          title: getString('module.blog.details.publication.date.datepicker.label'),
          sortField: 'datetime',
          titleClass: 'text-left sortable vcol-m',
          dataClass: 'text-left vcol-m text-ellipsis',
          callback: 'tableFormatTime',
        },
        {
          name: '__slot:locked',
          title: getString('content.lock.overview.title'),
          sortField: 'locked',
          titleClass: 'text-left sortable vcol-xs',
          dataClass: 'text-left vcol-xs text-ellipsis',
        },
      ],
      searchTerm: '',
      filters: {
        title: '',
        url: '',
        status: [],
      },
      hideFilters: true,
      preferences: {
        page: 1,
        perPage: 10,
        sortField: null,
        sortOrder: null,
      },
    };
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables';
</style>
